import { create } from 'zustand';

// ─── Types ────────────────────────────────────────────────────────────────────

export type GroupByMode = 'assignees' | 'teams' | 'teamWithUsers';
export type CompletionFilter = 'all' | 'incomplete' | 'completed';

// ─── Persistence ──────────────────────────────────────────────────────────────

const STORAGE_KEY_BOARD = 'rp_filter_state_board';
const STORAGE_KEY_WORKSPACE = 'rp_filter_state_workspace';
/** Legacy key — migrated on first load */
const STORAGE_KEY_LEGACY = 'rp_filter_state';

interface PersistedFilterState {
  groupByMode: GroupByMode;
  assignees: number[];
  teams: number[];
  selectedBoardIds: number[];
  completionFilter: CompletionFilter;
  showLeaves: boolean;
  showHolidays: boolean;
}

/** Current view scope — 'board' or 'workspace'. Set via setViewScope(). */
let _viewScope: 'board' | 'workspace' = 'board';

function storageKey(): string {
  return _viewScope === 'workspace' ? STORAGE_KEY_WORKSPACE : STORAGE_KEY_BOARD;
}

function loadPersistedFilters(): PersistedFilterState {
  const defaults: PersistedFilterState = {
    groupByMode: 'assignees',
    assignees: [],
    teams: [],
    selectedBoardIds: [],
    completionFilter: 'all',
    showLeaves: true,
    showHolidays: true,
  };
  try {
    // Try scoped key first, fall back to legacy key for migration
    let raw = localStorage.getItem(storageKey());
    if (!raw) {
      raw = localStorage.getItem(STORAGE_KEY_LEGACY);
    }
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedFilterState>;
      return {
        groupByMode: parsed.groupByMode ?? defaults.groupByMode,
        assignees: Array.isArray(parsed.assignees) ? parsed.assignees : [],
        teams: Array.isArray(parsed.teams) ? parsed.teams : [],
        selectedBoardIds: Array.isArray(parsed.selectedBoardIds) ? parsed.selectedBoardIds : [],
        completionFilter: parsed.completionFilter ?? defaults.completionFilter,
        showLeaves: parsed.showLeaves ?? defaults.showLeaves,
        showHolidays: parsed.showHolidays ?? defaults.showHolidays,
      };
    }
  } catch { /* ignore */ }
  return defaults;
}

function persistFilters(state: PersistedFilterState) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(state));
  } catch { /* ignore */ }
}

// ─── Store ────────────────────────────────────────────────────────────────────

interface FilterState {
  /** Group-by mode: assignees, teams, or teamWithUsers */
  groupByMode: GroupByMode;
  /** Selected assignee (user) IDs */
  assignees: number[];
  /** Selected team IDs */
  teams: number[];
  /** Selected role names */
  roles: string[];
  /** Selected board IDs for workspace multi-board filtering (empty = show all) */
  selectedBoardIds: number[];
  /** Completion status filter: show all, only incomplete, or only completed */
  completionFilter: CompletionFilter;
  /** Whether leave events are visible on the scheduler */
  showLeaves: boolean;
  /** Whether holiday events are visible on the scheduler */
  showHolidays: boolean;

  // ─── Actions ──────────────────────────────────────────────────────────────

  setGroupByMode: (mode: GroupByMode) => void;
  setAssignees: (ids: number[]) => void;
  setTeams: (ids: number[]) => void;
  setRoles: (roles: string[]) => void;
  setSelectedBoardIds: (ids: number[]) => void;
  setCompletionFilter: (filter: CompletionFilter) => void;
  setShowLeaves: (show: boolean) => void;
  setShowHolidays: (show: boolean) => void;
  /** Switch persistence scope and reload filters for that scope */
  setViewScope: (scope: 'board' | 'workspace') => void;
  clearFilters: () => void;
}

const initialFilters = loadPersistedFilters();

/** Helper: build the persisted state snapshot from current store state */
function getPersistedSnapshot(state: FilterState): PersistedFilterState {
  return {
    groupByMode: state.groupByMode,
    assignees: state.assignees,
    teams: state.teams,
    selectedBoardIds: state.selectedBoardIds,
    completionFilter: state.completionFilter,
    showLeaves: state.showLeaves,
    showHolidays: state.showHolidays,
  };
}

export const useFilterStore = create<FilterState>((set, get) => ({
  groupByMode: initialFilters.groupByMode,
  assignees: initialFilters.assignees,
  teams: initialFilters.teams,
  roles: [],
  selectedBoardIds: initialFilters.selectedBoardIds,
  completionFilter: initialFilters.completionFilter,
  showLeaves: initialFilters.showLeaves,
  showHolidays: initialFilters.showHolidays,

  setGroupByMode: (groupByMode) => {
    set({ groupByMode });
    persistFilters(getPersistedSnapshot(get()));
  },

  setAssignees: (assignees) => {
    set({ assignees });
    persistFilters(getPersistedSnapshot(get()));
  },

  setTeams: (teams) => {
    set({ teams });
    persistFilters(getPersistedSnapshot(get()));
  },

  setRoles: (roles) => set({ roles }),

  setSelectedBoardIds: (selectedBoardIds) => {
    set({ selectedBoardIds });
    persistFilters(getPersistedSnapshot(get()));
  },

  setCompletionFilter: (completionFilter) => {
    set({ completionFilter });
    persistFilters(getPersistedSnapshot(get()));
  },

  setShowLeaves: (showLeaves) => {
    set({ showLeaves });
    persistFilters(getPersistedSnapshot(get()));
  },

  setShowHolidays: (showHolidays) => {
    set({ showHolidays });
    persistFilters(getPersistedSnapshot(get()));
  },

  setViewScope: (scope) => {
    _viewScope = scope;
    const loaded = loadPersistedFilters();
    set({
      groupByMode: loaded.groupByMode,
      assignees: loaded.assignees,
      teams: loaded.teams,
      selectedBoardIds: loaded.selectedBoardIds,
      completionFilter: loaded.completionFilter,
      showLeaves: loaded.showLeaves,
      showHolidays: loaded.showHolidays,
      roles: [],
    });
  },

  clearFilters: () => {
    set({
      assignees: [],
      teams: [],
      roles: [],
      selectedBoardIds: [],
      completionFilter: 'all',
    });
    persistFilters(getPersistedSnapshot(get()));
  },
}));
